import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {PModuleMeasurement, PCrmModule} from '@/views/system/modules/interfaces';
import {PModuleType} from '@/enums/configs';
import {
  PMProcessActionType,
  PMProcessStatus,
  PMProcessSystemType,
} from '@/views/system/modules/enums';
import {EntityModule} from '../entityModule';
import {httpClient} from '@/api/baseApi';
import {getEventData, getEventTypes, getLastEvents, getSignal, getSpectrum} from './base';
import {SpectrumEventType, SpectrumYAxisType} from '@/enums/diagrams';
import {DateTimeInterval} from '@/utils/dateTimeItervalUtils';
import {PModuleStoreModule} from './interfaces';
import {isNumber} from '@/utils/commonUtils';

/*
    Динамический модуль хранилища vuex для работы с ПМ датчика тахометра по току
*/

const moduleName = 'pcrmStoreModule';

function transformGet(elem: any): PCrmModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    type: PModuleType.CrmModule,
    description: elem.description,
    processStatus: elem.process_status as PMProcessStatus,
    processStatusDescription: elem.process_status_description,
    status: elem.status,
    nodes: elem.nodes,
    alias: elem.alias,
    energyModuleGuid: elem.energy_module_guid,
    rotationValue: elem.rotation_value,
    iterations: elem.iterations,
    energyWorkValue: elem.energy_work_value,
    event_name: elem.event_name,
    field_name: elem.field_name,
    measurements: [],
  };
}

function getMeasurements(elem: any): PModuleMeasurement[] {
  return elem.measurements.map((elem: any) => ({
    measurementId: elem.module_type_measurement_id,
    measurementDescription: elem.module_type_measurement_description,
    topWarningBoundary: elem.top_warning_boundary,
    topDangerBoundary: elem.top_danger_boundary,
    topErrorBoundary: elem.top_error_boundary,
    bottomWarningBoundary: elem.bottom_warning_boundary,
    bottomDangerBoundary: elem.bottom_danger_boundary,
    bottomErrorBoundary: elem.bottom_error_boundary,
  }));
}

function transformGetById(elem: any): PCrmModule {
  const model = transformGet(elem);
  model.measurements = getMeasurements(elem);
  return model;
}

function transformCreateEdit(elem: any): PCrmModule {
  const measurements: PModuleMeasurement[] = getMeasurements(elem);

  return {
    name: elem.name,
    alias: elem.alias,
    type: PModuleType.CrmModule,
    description: elem.description,
    nodes: elem.nodes,
    energyModuleGuid: elem.energy_module_guid,
    rotationValue: elem.rotation_value,
    iterations: elem.iterations,
    energyWorkValue: elem.energy_work_value,
    event_name: elem.event_name,
    field_name: elem.field_name,
    measurements: measurements,
  };
}

function transformSave(model: any): any {
  const measurements: any[] = model.measurements.map((elem: any) => {
    const m: any = {
      module_type_measurement_id: elem.measurementId,
      module_type_measurement_description: elem.measurementDescription,
    };
    if (isNumber(elem.topWarningBoundary)) m.top_warning_boundary = elem.topWarningBoundary;
    if (isNumber(elem.topDangerBoundary)) m.top_danger_boundary = elem.topDangerBoundary;
    if (isNumber(elem.topErrorBoundary)) m.top_error_boundary = elem.topErrorBoundary;
    if (isNumber(elem.bottomWarningBoundary))
      m.bottom_warning_boundary = elem.bottomWarningBoundary;
    if (isNumber(elem.bottomDangerBoundary)) m.bottom_danger_boundary = elem.bottomDangerBoundary;
    if (isNumber(elem.bottomErrorBoundary)) m.bottom_error_boundary = elem.bottomErrorBoundary;
    return m;
  });

  return {
    name: model.name,
    description: model.description,
    node_id: model.nodeId ? model.nodeId : model.node_id,
    energy_module_guid: model.energyModuleGuid ? model.energyModuleGuid : model.energy_module_guid,
    rotation_value: model.rotationValue ? model.rotationValue : model.rotation_value,
    iterations: model.iterations,
    energy_work_value: model.energyWorkValue ? model.energyWorkValue : model.energy_work_value,
    event_name: model.event_name,
    field_name: model.field_name,
    measurements: measurements,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class PCrmStoreModule extends EntityModule implements PModuleStoreModule {
  protected routeName = 'crm-modules';

  get all(): PCrmModule[] {
    return super.all as PCrmModule[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: PCrmModule}> {
    return super.create(transformCreateEdit) as Promise<{data: PCrmModule}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: PCrmModule}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: PCrmModule}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: PCrmModule) {
    return super.save(model, transformSave);
  }

  //Получение объекта по идентификатору
  @Action
  async getById(id: any): Promise<{data: PCrmModule}> {
    return super.getById(id, transformGetById) as Promise<{data: PCrmModule}>;
  }

  //Получение сигнала
  @Action
  async getSignal(params: {
    id: number;
    dateInterval: DateTimeInterval;
    fieldAlias: string;
  }): Promise<any> {
    try {
      return await getSignal(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение спектра
  @Action
  async getSpectrum(params: {
    id: number;
    spectrumType: SpectrumEventType;
    date?: number;
    offset?: number;
    length?: number;
    spectrumValueType?: SpectrumYAxisType;
  }): Promise<any> {
    try {
      return await getSpectrum(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  @Action
  async processAction(params: {id: number; action: PMProcessActionType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/`, {
      action: params.action,
    });
    return data;
  }

  //Выполнить системную команду программного модуля
  @Action
  async processSystem(params: {id: number; action: PMProcessSystemType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/system`, {
      action: params.action,
    });
    return data;
  }

  //Получение типов событий
  @Action
  async getEventTypes(): Promise<any> {
    try {
      return await getEventTypes(this.routeName);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение списка последних событий
  @Action
  async getLastEvents(params: {id: number; eventType: string; limit?: number}): Promise<any> {
    try {
      return await getLastEvents(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение данных события
  @Action
  async getEventData(params: {id: number; eventType: string; timestamp: number}): Promise<any> {
    try {
      return await getEventData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(PCrmStoreModule);
